import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ParametersService} from './parameters.service';
import {Language, LanguageShortcut, Subtitles, SubtitlesData} from "../models";
import {BehaviorSubject, Observable, Subscription} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class SubtitlesService implements OnDestroy {
    private subtitles: Subtitles = {};
    downloaded: boolean = false;
    subtitlesChanged = new BehaviorSubject(false);
    subtitlesChanged$ = this.subtitlesChanged.asObservable();
    languagesList = this.getLanguagesList();
    language: Language = '';
    languageName: string = '';
    languageTag: string = '';
    subtitlesPromise: Observable<any>;
    private setSubtitlesSubscription: Subscription;

    constructor(
        private http: HttpClient,
        private parameters: ParametersService
    ) {
        this.languageTag = window.localStorage.getItem('languageTag') || '';
    }

    ngOnDestroy() {
        if (this.setSubtitlesSubscription) {
            this.setSubtitlesSubscription.unsubscribe();
        }
    }

    getSubtitles(shortcutOrDefault: Language, tag = '') {
        this.setLanguage(shortcutOrDefault);
        this.languageTag = tag || this.getCompanyTag();

        const savedTag = window.localStorage.getItem('languageTag');
        if (savedTag !== this.languageTag) {
            window.localStorage.setItem('languageTag', this.languageTag);
        }

        if (this.hasSavedSubtitles()) {
            this.setSubtitlesFromLocalStorage();
            this.downloaded = true;
            this.subtitlesChanged.next(true);
        }

        if (this.downloaded === false || !this.hasSavedSubtitles()) {
            if (!this.language) {
                this.language = LanguageShortcut.en;
            }

            this.subtitlesPromise = this.http.get(`/api/v1/i18n/${this.language}${this.getTagParams()}`);

            this.setSubtitlesSubscription = this.subtitlesPromise.subscribe({
                next: (response: any) => {
                    this.subtitles = response.data;
                    this.downloaded = true;
                    this.saveSubtitlesToLocalStorage();
                    this.subtitlesChanged.next(true);
                },
                error: () => {
                    console.error('no subtitles');
                },
            });
        }
    }

    saveSubtitlesToLocalStorage() {
        const subtitlesData = {
            lastUpdate: Date.now(),
            urlParameters: `${this.language}${this.getTagParams()}`,
            languageTag: this.languageTag,
            data: this.subtitles,
        }
        window.localStorage.setItem('subtitlesData', JSON.stringify(subtitlesData));
        window.localStorage.setItem('languageTag', this.languageTag);
    }

    private setSubtitlesFromLocalStorage() {
        const subtitlesData = window.localStorage.getItem('subtitlesData');
        const parsedSubtitlesData = this.parseSubtitles(subtitlesData);
        if (parsedSubtitlesData.data) {
            this.subtitles = parsedSubtitlesData.data;
        }
    }

    private parseSubtitles(subtitlesData: string): SubtitlesData | null {
        try {
            return JSON.parse(subtitlesData);
        } catch (error) {
            return null;
        }
    }

    private isSubtitlesDataDeprecated(lastUpdate = 0) {
        return Date.now() - lastUpdate > 172_800_000; // two days
    }

    hasSavedSubtitles() {
        const subtitlesData = window.localStorage.getItem('subtitlesData');
        if (subtitlesData !== null) {
            const parsedSubtitlesData = this.parseSubtitles(subtitlesData);
            const deprecated = this.isSubtitlesDataDeprecated(parsedSubtitlesData.lastUpdate);
            const urlParameters = `${this.language}${this.getTagParams()}`;

            return parsedSubtitlesData.urlParameters === urlParameters && !deprecated
        }
        return false
    }

    setSubtitle(id: number): string {
        return this.subtitles[id] ? this.subtitles[id] : '';
    }

    setWiki(id: number): string {
        if (id > 121) {
            return this.subtitles[id];
        } else {
            return this.subtitles[id + 4497];
        }
    }

    private setLanguage(shortcutOrDefault: Language): void {
        if (shortcutOrDefault === 'default') {
            const id = parseInt(localStorage.getItem('selectedLanguageId') || '', 10);

            if (!isNaN(id)) {
                this.selectLanguage(id);
            } else {
                this.selectLanguage(this.getClientLanguage().id);
            }
        } else {
            const language = this.languagesList.find(lang => lang.shortcut === shortcutOrDefault);

            if (language) {
                this.selectLanguage(language.id);
            } else {
                const defaultLanguage = this.languagesList.find(lang => lang.shortcut === 'en');
                this.selectLanguage(defaultLanguage.id);
            }
        }
    }

    private selectLanguage(id: number): void {
        const selectedLanguageId = localStorage.getItem('selectedLanguageId');
        if (id !== parseInt(selectedLanguageId || '', 10)) {
            localStorage.setItem('selectedLanguageId', id.toString());
        }

        const language = this.languagesList.find(lang => lang.id === id);
        if (language) {
            this.language = language.shortcut;
            this.languageName = language.name;
        }
    }

    private getClientLanguage(): any {
        let selectedLanguage = this.languagesList[0];

        const clientLanguage = window.navigator.language.split('-')[0];
        const language = this.languagesList.find(lang => lang.shortcut === clientLanguage);
        if (language) {
            selectedLanguage = language;
        }

        return selectedLanguage;
    }

    getTagParams() {
        return this.languageTag ? `/${this.languageTag}` : '';
    }

    private getCompanyTag(): string {
        let tag = '';
        const companySubdomains = ['opop', 'kolton', 'klimor', 'roth', 'polypipe', 'jawo'];

        companySubdomains.forEach(companyTag => {
            if (window.location.hostname.includes(companyTag)) {
                tag = companyTag;
            }
        });

        return tag;
    }

    private getListTypeFromUrl(): string {
        let listTypeFromUrl = '';

        if (window.location.hostname.includes('emodul.pl')) {
            listTypeFromUrl = 'defro';
        } else if (window.location.hostname.includes('emodul.tech')) {
            listTypeFromUrl = 'russia';
        } else {
            const company = this.parameters.urlCompany;
            const ignoredCompanies = ['tech', 'app', 'oeg', 'kolton', 'verano'];

            if (!ignoredCompanies.includes(company)) {
                listTypeFromUrl = company;
            }
        }

        return listTypeFromUrl;
    }

    private getLanguagesList(): any[] {
        const listType = this.getListTypeFromUrl();

        switch (listType) {
            case 'defro':
                return [
                    {id: 1, shortcut: 'pl', name: 'Polski'},
                    {id: 2, shortcut: 'en', name: 'English'},
                    {id: 5, shortcut: 'de', name: 'Deutsch'},
                    {id: 3, shortcut: 'cs', name: 'Čeština'},
                    {id: 4, shortcut: 'sk', name: 'Slovenský'},
                    {id: 6, shortcut: 'hu', name: 'Magyar'},
                    {id: 8, shortcut: 'ru', name: 'Pусский'}
                ];
            case 'jawo':
                return [
                    {id: 5, shortcut: 'de', name: 'Deutsch'},
                    {id: 2, shortcut: 'en', name: 'English'},
                    {id: 1, shortcut: 'pl', name: 'Polski'},
                    {id: 9, shortcut: 'fr', name: 'Français'}
                ];
            case 'roth':
                return [
                    {id: 2, shortcut: 'en', name: 'English'},
                    {id: 5, shortcut: 'de', name: 'Deutsch'},
                    {id: 17, shortcut: 'se', name: 'Swedish'},
                    {id: 18, shortcut: 'fi', name: 'Finnish'},
                    {id: 22, shortcut: 'dk', name: 'Danish'},
                    {id: 29, shortcut: 'no', name: 'Norwegian'},
                    {id: 16, shortcut: 'es', name: 'Español'},
                    {id: 1, shortcut: 'pl', name: 'Polish'}
                ];
            // Add other cases as needed...
            default:
                return [
                    {id: 1, shortcut: 'pl', name: 'Polski'},
                    {id: 2, shortcut: 'en', name: 'English'},
                    {id: 5, shortcut: 'de', name: 'Deutsch'},
                    {id: 9, shortcut: 'fr', name: 'Français'},
                    {id: 21, shortcut: 'nl', name: 'Nederlands'},
                    {id: 3, shortcut: 'cs', name: 'Čeština'},
                    {id: 4, shortcut: 'sk', name: 'Slovenský'},
                    {id: 6, shortcut: 'hu', name: 'Magyar'},
                    {id: 12, shortcut: 'it', name: 'Italiano'},
                    {id: 16, shortcut: 'es', name: 'Español'},
                    {id: 13, shortcut: 'et', name: 'Eesti'},
                    {id: 20, shortcut: 'ro', name: 'Română'},
                    {id: 11, shortcut: 'lt', name: 'Lietuvos'},
                    {id: 7, shortcut: 'ua', name: 'Український'},
                    {id: 8, shortcut: 'ru', name: 'Pусский'}
                ];
        }
    }
}
